import { queryOptions, useQuery } from "@tanstack/react-query";
import * as z from "zod";
import { api } from "../http";
import { paginatedListSchema } from "@/lib/utils/zod";

export const departmentFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().min(1, "Description is required"),
});

export const departmentSchema = departmentFormSchema.extend({
  id: z.number(),
});

export const departmentsListSchema = paginatedListSchema(departmentSchema);

export type DepartmentsList = z.infer<typeof departmentsListSchema>;
export type DepartmentFormData = z.infer<typeof departmentFormSchema>;
export type Department = z.infer<typeof departmentSchema>;

type SearchParams = {
  page: number;
  per_page?: number;
  search?: string;
};

export function departmentsListQueryOptions(params: SearchParams) {
  return queryOptions({
    queryKey: ["departments", "list", params],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        per_page: (params.per_page || 10).toString(),
      });
      if (params.search) searchParams.set("search", params.search);

      const response = await api.raw
        .get("api/v1/departments/paginated", {
          searchParams,
        })
        .json<DepartmentsList>();
      return response;
    },
  });
}

export function departmentQueryOptions(id: number) {
  return queryOptions({
    queryKey: ["departments", "detail", id],
    queryFn: async () => {
      const response = await api.raw
        .get(`api/v1/departments/${id}`)
        .json<Department>();
      return response;
    },
  });
}

export function departmentsAllQueryOptions() {
  return queryOptions({
    queryKey: ["departments", "list"],
    queryFn: async () => {
      const response = await api.raw
        .get("api/v1/departments/")
        .json<Array<Department>>();
      return response;
    },
  });
}

// Hooks
export function useDepartmentsList(params: SearchParams) {
  return useQuery(departmentsListQueryOptions(params));
}

export function useDepartment(id: number) {
  return useQuery(departmentQueryOptions(id));
}

export function useDepartmentsAll() {
  return useQuery(departmentsAllQueryOptions());
}
